import type {
  PlannerImportCommitResponse,
  PlannerImportPreviewResponse,
  UserPreferences,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * V1.5 Settings-Page (Phase 10).
 * Quelle: Brief §10 Phase 10 + 05_implementation_session2.md §10.7.
 *
 * Sections:
 *   - Theme (mindsquare / mission-control) · Live-Preview via ThemeProvider
 *   - Sprache (de / en)
 *   - Density (calm / balanced / dense) · CSS [data-density]
 *   - AI prominence (home / copilot / feature) · nur prefs, kein konkretes Verhalten
 *   - Default Phase Lens (pipeline / kanban / matrix)
 *
 * Persistenz: PATCH /v1/users/me/preferences. Optimistic update via
 * queryClient.setQueryData. Theme/Density werden parallel im ThemeProvider
 * gesetzt (Live-Preview), Sprache via i18n.changeLanguage.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/toast.js';
import { useConfirm } from '../../components/v2/confirm-dialog.js';
import { type Density, type Theme, useTheme } from '../../components/v2/theme-provider.js';
import { ApiError, api } from '../../lib/api.js';
import { useFeatureFlag } from '../../lib/features.js';
import { useSession } from '../../lib/session.js';
import { PREFERENCES_QUERY_KEY } from '../../lib/sticky-filters.js';

type AiProminence = 'home' | 'copilot' | 'feature';
type Lens = 'pipeline' | 'kanban' | 'matrix';

export function SettingsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const { user } = useSession();
  // Manuelles Re-Sync per SF-Id: Admin ODER kaufm. Leitung (Funktionsrolle).
  // Bewusst NICHT über Persona — usePersona().available enthält 'kl' als
  // Reader-Fallback für jeden User.
  const canResync =
    !!user && (user.is_admin || user.function_roles.some((r) => r.role === 'kaufm_leitung'));

  // Planner-Import: Admin ODER kaufm. Leitung ODER Bereichsleiter.
  const canImportPlanner =
    !!user &&
    (user.is_admin ||
      user.function_roles.some((r) => r.role === 'kaufm_leitung' || r.role === 'bereichsleiter'));
  // #122: Der Import ist standardmäßig abgeschaltet. Aus → Abschnitt samt Historie weg,
  // auch für Admin/KFML/BL; die Daten bleiben in der DB und kommen beim Einschalten zurück.
  const plannerImportAktiv = useFeatureFlag('planner_import');

  const prefsQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => api.getMyPreferences(),
  });
  const prefs = prefsQuery.data?.preferences ?? {};

  const mutation = useMutation({
    mutationFn: (patch: UserPreferences) => api.updateMyPreferences(patch),
    onSuccess: (res) => {
      queryClient.setQueryData(PREFERENCES_QUERY_KEY, res);
      toast.success(i18n.t('v2.settings.saved', 'Einstellung gespeichert'));
    },
    onError: (err) =>
      toast.error(
        i18n.t('v2.settings.save_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const persist = (patch: UserPreferences) => mutation.mutate(patch);

  const currentTheme: Theme = (prefs.theme as Theme | undefined) ?? theme.theme;
  const currentDensity: Density = (prefs.density as Density | undefined) ?? theme.density;
  const currentLocale = (prefs.locale ?? (i18n.language.startsWith('de') ? 'de' : 'en')) as
    | 'de'
    | 'en';
  const currentAi = (prefs.ai_prominence ?? 'home') as AiProminence;
  const currentLens = (prefs.default_phase_lens ?? 'pipeline') as Lens;

  return (
    <div className="max-w-3xl mx-auto px-2 pb-12 space-y-8">
      <header className="pt-6 animate-fade-up">
        <div
          className="text-[10.5px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {i18n.t('v2.settings.title', 'Einstellungen')}
        </div>
        <h1
          className="font-medium leading-[1.05] tracking-tight"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(22px, 2.4vw, 36px)',
          }}
        >
          {i18n.t('v2.settings.hero_lead', 'Deine')}{' '}
          <em
            className="not-italic"
            style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {i18n.t('v2.settings.hero_emphasis', 'Arbeitsumgebung')}
          </em>
          {i18n.t('v2.settings.hero_trail', '.')}
        </h1>
        <p className="text-sm max-w-[640px] mt-2" style={{ color: 'var(--ink-3)' }}>
          {i18n.t(
            'v2.settings.subtitle',
            'Theme, Sprache, Dichte und AI-Prominenz. Änderungen wirken live, gespeichert pro User.',
          )}
        </p>
      </header>

      <SettingsSection
        label={i18n.t('v2.settings.section_theme', 'Theme')}
        hint={i18n.t(
          'v2.settings.section_theme_hint',
          'Hell + warm oder dunkel + technisch. Live-Preview.',
        )}
      >
        <SegmentedToggle
          value={currentTheme}
          options={[
            {
              id: 'mindsquare',
              label: i18n.t('v2.settings.theme_mindsquare', 'Mindsquare (hell)'),
            },
            {
              id: 'mission-control',
              label: i18n.t('v2.settings.theme_mission_control', 'Mission Control (dunkel)'),
            },
          ]}
          onChange={(v) => {
            theme.setTheme(v as Theme);
            persist({ theme: v as Theme });
          }}
        />
      </SettingsSection>

      <SettingsSection
        label={i18n.t('v2.settings.section_language', 'Sprache')}
        hint={i18n.t(
          'v2.settings.section_language_hint',
          'UI-Sprache. Phasennamen + Domänenbegriffe bleiben DE.',
        )}
      >
        <SegmentedToggle
          value={currentLocale}
          options={[
            { id: 'de', label: 'Deutsch' },
            { id: 'en', label: 'English' },
          ]}
          onChange={(v) => {
            void i18n.changeLanguage(v);
            persist({ locale: v as 'de' | 'en' });
          }}
        />
      </SettingsSection>

      <SettingsSection
        label={i18n.t('v2.settings.section_density', 'Dichte')}
        hint={i18n.t('v2.settings.section_density_hint', 'Wie viel Luft zwischen den Elementen?')}
      >
        <SegmentedToggle
          value={currentDensity}
          options={[
            { id: 'calm', label: i18n.t('v2.settings.density_calm', 'Calm') },
            { id: 'balanced', label: i18n.t('v2.settings.density_balanced', 'Balanced') },
            { id: 'dense', label: i18n.t('v2.settings.density_dense', 'Dense') },
          ]}
          onChange={(v) => {
            theme.setDensity(v as Density);
            persist({ density: v as Density });
          }}
        />
      </SettingsSection>

      <SettingsSection
        label={i18n.t('v2.settings.section_ai', 'AI-Prominenz')}
        hint={i18n.t(
          'v2.settings.section_ai_hint',
          'Wie zentral soll der KI-Briefing-Strip in Mission Control sein?',
        )}
      >
        <SegmentedToggle
          value={currentAi}
          options={[
            { id: 'home', label: i18n.t('v2.settings.ai_home', 'Home (Default)') },
            { id: 'copilot', label: i18n.t('v2.settings.ai_copilot', 'Copilot (Seitenleiste)') },
            { id: 'feature', label: i18n.t('v2.settings.ai_feature', 'Feature (groß)') },
          ]}
          onChange={(v) => persist({ ai_prominence: v as AiProminence })}
        />
      </SettingsSection>

      <SettingsSection
        label={i18n.t('v2.settings.section_lens', 'Phasen-Linse · Default')}
        hint={i18n.t(
          'v2.settings.section_lens_hint',
          'Welche Linse soll das Phase Board beim Öffnen zeigen?',
        )}
      >
        <SegmentedToggle
          value={currentLens}
          options={[
            { id: 'pipeline', label: i18n.t('v2.board.lens_pipeline', 'Pipeline') },
            { id: 'kanban', label: i18n.t('v2.board.lens_kanban', 'Kanban') },
            { id: 'matrix', label: i18n.t('v2.board.lens_matrix', 'Matrix') },
          ]}
          onChange={(v) => persist({ default_phase_lens: v as Lens })}
        />
      </SettingsSection>

      <McpSection />

      {canResync && <SalesforceResyncSection />}

      {canImportPlanner && plannerImportAktiv && <PlannerImportSection />}

      {mutation.isPending && (
        <div className="text-xs" style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}>
          {i18n.t('v2.settings.saving', 'speichert…')}
        </div>
      )}
    </div>
  );
}

/**
 * MCP-Verbindungs-Sektion (Das Orakel): zeigt Status und bietet Connect/Disconnect/Test.
 * 503 mcp_not_configured → zeigt "MCP nicht konfiguriert" (kein Crash).
 */
function McpSection() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const confirmAction = useConfirm();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: ['mcp-status'],
    queryFn: () => api.getMcpStatus(),
    // On 503 (mcp_not_configured) we want to catch gracefully, so treat it as data
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 503) return false;
      return failureCount < 2;
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.testMcpConnection(),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(
          i18n.t('v2.settings.mcp_test_success', 'Verbindung OK'),
          res.tools !== undefined
            ? i18n.t('v2.settings.mcp_test_tools', '{{count}} Tools verfügbar', {
                count: res.tools,
              })
            : undefined,
        );
      } else {
        toast.error(i18n.t('v2.settings.mcp_test_failed', 'Test fehlgeschlagen'));
      }
    },
    onError: (err) =>
      toast.error(
        i18n.t('v2.settings.mcp_test_failed', 'Test fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api.disconnectMcp(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mcp-status'] });
      toast.success(i18n.t('v2.settings.mcp_disconnected', 'MCP-Verbindung getrennt'));
    },
    onError: (err) =>
      toast.error(
        i18n.t('v2.settings.mcp_disconnect_failed', 'Trennen fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });

  const handleConnect = () => {
    window.location.href = api.getMcpConnectUrl('/einstellungen');
  };

  const handleDisconnect = () => {
    void confirmAction({
      title: i18n.t('v2.settings.mcp_disconnect_confirm', 'MCP-Verbindung wirklich trennen?'),
      tone: 'danger',
      confirmLabel: i18n.t('v2.settings.mcp_disconnect_btn', 'Trennen'),
    }).then((ok) => {
      if (ok) disconnectMutation.mutate();
    });
  };

  // 503 mcp_not_configured
  const isMcpNotConfigured =
    statusQuery.isError &&
    statusQuery.error instanceof ApiError &&
    statusQuery.error.status === 503;

  const renderContent = () => {
    if (isMcpNotConfigured) {
      return (
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {i18n.t(
            'v2.settings.mcp_not_configured',
            'MCP ist auf diesem Server nicht konfiguriert.',
          )}
        </p>
      );
    }

    if (statusQuery.isLoading) {
      return (
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {i18n.t('v2.settings.mcp_loading', 'Lade Status…')}
        </p>
      );
    }

    if (statusQuery.isError) {
      return (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {i18n.t('v2.settings.mcp_status_error', 'Status konnte nicht geladen werden.')}
        </p>
      );
    }

    const data = statusQuery.data;
    if (!data) return null;
    const { status, subject, connected_at } = data;

    if (status === 'connected') {
      return (
        <div className="space-y-3">
          <div
            className="inline-flex items-center gap-2 text-xs rounded-full px-3 py-1"
            style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: 'var(--success, #22c55e)', flexShrink: 0 }}
              aria-hidden="true"
            />
            {i18n.t('v2.settings.mcp_status_connected', 'Verbunden')}
          </div>
          {(subject || connected_at) && (
            <div
              className="text-xs space-y-0.5"
              style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
            >
              {subject && (
                <div>
                  {i18n.t('v2.settings.mcp_subject_label', 'Account:')} <strong>{subject}</strong>
                </div>
              )}
              {connected_at && (
                <div>
                  {i18n.t('v2.settings.mcp_connected_at_label', 'Verbunden seit:')}{' '}
                  <strong>{new Date(connected_at).toLocaleString()}</strong>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="px-3 py-1 rounded text-sm disabled:opacity-50"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--ink-2)',
                border: '1px solid var(--line-soft)',
              }}
            >
              {testMutation.isPending
                ? i18n.t('v2.settings.mcp_testing', 'Teste…')
                : i18n.t('v2.settings.mcp_test_btn', 'Verbindung testen')}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              className="px-3 py-1 rounded text-sm disabled:opacity-50"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--danger, #ef4444)',
                border: '1px solid var(--line-soft)',
              }}
            >
              {disconnectMutation.isPending
                ? i18n.t('v2.settings.mcp_disconnecting', 'Trennt…')
                : i18n.t('v2.settings.mcp_disconnect_btn', 'Trennen')}
            </button>
          </div>
        </div>
      );
    }

    // none / unavailable / revoked
    // Widerruf verlangt Neu-Login; eine Stoerung nicht (PV-045).
    const isWarning = status === 'revoked';
    return (
      <div className="space-y-3">
        <div
          className="inline-flex items-center gap-2 text-xs rounded-full px-3 py-1"
          style={{
            background: 'var(--surface-2)',
            color: isWarning ? 'var(--warning, #f59e0b)' : 'var(--ink-3)',
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: isWarning ? 'var(--warning, #f59e0b)' : 'var(--ink-3)',
              flexShrink: 0,
            }}
            aria-hidden="true"
          />
          {status === 'unavailable'
            ? i18n.t('v2.settings.mcp_status_unavailable', 'Gerade nicht erreichbar')
            : status === 'revoked'
              ? i18n.t('v2.settings.mcp_status_revoked', 'Widerrufen')
              : i18n.t('v2.settings.mcp_status_none', 'Nicht verbunden')}
        </div>
        {isWarning && (
          <p className="text-xs" style={{ color: 'var(--warning, #f59e0b)' }}>
            {i18n.t(
              'v2.settings.mcp_reauth_hint',
              'Bitte erneut verbinden, um das Orakel zu reaktivieren.',
            )}
          </p>
        )}
        {status === 'unavailable' && (
          <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
            {i18n.t(
              'v2.settings.mcp_unavailable_hint',
              'Das Orakel ist gerade nicht erreichbar. Die Verbindung bleibt bestehen und wird automatisch erneut versucht — du musst nichts tun.',
            )}
          </p>
        )}
        <button
          type="button"
          onClick={handleConnect}
          className="px-3 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] text-sm"
        >
          {isWarning
            ? i18n.t('v2.settings.mcp_reconnect_btn', 'Neu verbinden')
            : i18n.t('v2.settings.mcp_connect_btn', 'Verbinden')}
        </button>
      </div>
    );
  };

  return (
    <SettingsSection
      label={i18n.t('v2.settings.section_mcp', 'MCP-Verbindung (Das Orakel)')}
      hint={i18n.t(
        'v2.settings.section_mcp_hint',
        'Microsoft 365 / Outlook-Verbindung für KI-Mission-Briefings. OAuth via Entra ID.',
      )}
    >
      {renderContent()}
    </SettingsSection>
  );
}

/**
 * Manuelles Re-Sync ausgewählter Salesforce-Projekte per Id (komma-/zeilen-/
 * leerzeichen-separiert). Sichtbar für Admin + kaufm. Leitung; serverseitig via
 * requireFunctionRole('kaufm_leitung') abgesichert.
 */
function SalesforceResyncSection() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const [raw, setRaw] = useState('');

  const ids = raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const mutation = useMutation({
    mutationFn: () => api.resyncByIds(ids),
    onError: (err) =>
      toast.error(
        i18n.t('v2.settings.resync_failed', 'Re-Sync fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <SettingsSection
      label={i18n.t('v2.settings.section_resync', 'Salesforce · Projekte neu syncen')}
      hint={i18n.t(
        'v2.settings.section_resync_hint',
        'Eine oder mehrere SF-ProjektIDs (komma- oder zeilensepariert) gezielt neu laden.',
      )}
    >
      <div className="space-y-2">
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={3}
          placeholder="a0X1t000000ABCD, a0X1t000000EFGH"
          className="w-full border rounded px-2 py-1.5 text-sm font-mono"
          style={{
            borderColor: 'var(--line-soft)',
            background: 'var(--surface-2)',
            color: 'var(--ink)',
          }}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={ids.length === 0 || mutation.isPending}
            className="px-3 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] text-sm disabled:opacity-50"
          >
            {mutation.isPending
              ? i18n.t('v2.settings.resync_running', 'läuft…')
              : i18n.t('v2.settings.resync_btn', 'Neu syncen')}
          </button>
          <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
            {i18n.t('v2.settings.resync_count', '{{count}} ID(s)', { count: ids.length })}
          </span>
        </div>
        {mutation.data && (
          <div
            className="text-xs rounded-lg p-2 space-y-1"
            style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          >
            <div>
              {i18n.t('v2.settings.resync_status', 'Status:')}{' '}
              <strong>{mutation.data.status}</strong> · fetched: {mutation.data.counts.fetched} ·
              created: {mutation.data.counts.created} · updated: {mutation.data.counts.updated} ·
              errors: {mutation.data.counts.errors}
            </div>
            {mutation.data.errors.length > 0 && (
              <ul className="list-disc pl-4" style={{ color: 'var(--danger)' }}>
                {mutation.data.errors.slice(0, 10).map((e, i) => (
                  <li key={`${e.salesforce_object_id}-${i}`}>
                    <code>{e.salesforce_object_id || '–'}</code>: {e.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </SettingsSection>
  );
}

/**
 * Planner-Import: Datei hochladen, Vorschau prüfen, Import bestätigen, Historie anzeigen.
 * Sichtbar für Admin + kaufm. Leitung + Bereichsleiter (canImportPlanner-Gate in SettingsPage).
 */
function PlannerImportSection() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PlannerImportPreviewResponse | null>(null);
  const [result, setResult] = useState<PlannerImportCommitResponse | null>(null);

  const previewMutation = useMutation({
    mutationFn: (f: File) => api.previewPlannerImport(f),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
    },
    onError: (err) =>
      toast.error(
        i18n.t('v2.settings.plannerImport.preview_failed', 'Vorschau fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const commitMutation = useMutation({
    mutationFn: (runId: string) => api.commitPlannerImport(runId),
    onSuccess: (data) => {
      setResult(data);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ['planner-import-runs'] });
    },
    onError: (err) =>
      toast.error(
        i18n.t('v2.settings.plannerImport.commit_failed', 'Import fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <SettingsSection
      label={i18n.t('v2.settings.plannerImport.title', 'Aus Planner importieren')}
      hint={i18n.t(
        'v2.settings.plannerImport.section_hint',
        'Excel-Datei aus Microsoft Planner hochladen, Änderungen prüfen und gezielt übernehmen.',
      )}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
              setResult(null);
            }}
            className="block text-sm"
            style={{ color: 'var(--ink-2)' }}
          />
          <button
            type="button"
            disabled={!file || previewMutation.isPending}
            onClick={() => {
              if (file) previewMutation.mutate(file);
            }}
            className="px-3 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] text-sm disabled:opacity-50"
          >
            {previewMutation.isPending
              ? i18n.t('v2.settings.plannerImport.preview_running', 'Prüft…')
              : i18n.t('v2.settings.plannerImport.previewButton', 'Vorschau prüfen')}
          </button>
        </div>

        {preview && !result && (
          <div
            className="rounded-lg p-3 space-y-3"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
              {i18n.t(
                'v2.settings.plannerImport.changesCount',
                '{{count}} Projekte werden verarbeitet.',
                { count: preview.changes.length },
              )}
            </p>
            {preview.log.length > 0 && (
              <ul className="text-xs space-y-1 list-disc pl-4" style={{ color: 'var(--ink-3)' }}>
                {preview.log.map((entry, i) => (
                  <li key={`${entry.category}-${entry.salesforce_id ?? entry.row_ref}-${i}`}>
                    <code>{entry.category}</code>
                    {entry.row_ref ? ` [${entry.row_ref}]` : ''}: {entry.message}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              disabled={commitMutation.isPending}
              onClick={() => commitMutation.mutate(preview.run_id)}
              className="px-3 py-1 rounded bg-[var(--primary)] text-[color:var(--on-primary)] text-sm disabled:opacity-50"
            >
              {commitMutation.isPending
                ? i18n.t('v2.settings.plannerImport.commit_running', 'Importiert…')
                : i18n.t('v2.settings.plannerImport.commitButton', 'Import bestätigen')}
            </button>
          </div>
        )}

        {result && (
          <div
            className="rounded-lg p-3 space-y-3"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
              {i18n.t(
                'v2.settings.plannerImport.resultSummary',
                '{{processed}} verarbeitet, {{skipped}} übersprungen.',
                { processed: result.processed_count, skipped: result.skipped_count },
              )}
            </p>
            {result.log.length > 0 && (
              <ul className="text-xs space-y-1 list-disc pl-4" style={{ color: 'var(--ink-3)' }}>
                {result.log.map((entry, i) => (
                  <li key={`${entry.category}-${entry.salesforce_id ?? entry.row_ref}-${i}`}>
                    <code>{entry.category}</code>
                    {entry.row_ref ? ` [${entry.row_ref}]` : ''}: {entry.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <PlannerImportHistory />
      </div>
    </SettingsSection>
  );
}

function PlannerImportHistory() {
  const { i18n } = useTranslation();
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ['planner-import-runs'],
    queryFn: () => api.listPlannerImportRuns(),
  });

  const runDetailQuery = useQuery({
    queryKey: ['planner-import-run', openRunId],
    queryFn: () => {
      if (!openRunId) throw new Error('no runId');
      return api.getPlannerImportRun(openRunId);
    },
    enabled: openRunId !== null,
  });

  const runs = runsQuery.data?.runs ?? [];

  return (
    <div className="space-y-2">
      <h3
        className="text-xs font-medium"
        style={{ color: 'var(--ink-2)', fontFamily: 'var(--f-mono)' }}
      >
        {i18n.t('v2.settings.plannerImport.historyTitle', 'Bisherige Importe')}
      </h3>

      {runsQuery.isLoading && (
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {i18n.t('v2.common.loading', 'Lädt…')}
        </p>
      )}

      {!runsQuery.isLoading && runs.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {i18n.t('v2.settings.plannerImport.history_empty', 'Noch keine Importe.')}
        </p>
      )}

      {runs.length > 0 && (
        <div className="rounded border overflow-hidden" style={{ borderColor: 'var(--line-soft)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }}>
                <th className="px-2 py-1 text-left font-normal">
                  {i18n.t('v2.settings.plannerImport.col_date', 'Datum')}
                </th>
                <th className="px-2 py-1 text-left font-normal">
                  {i18n.t('v2.settings.plannerImport.col_file', 'Datei')}
                </th>
                <th className="px-2 py-1 text-left font-normal">
                  {i18n.t('v2.settings.plannerImport.col_status', 'Status')}
                </th>
                <th className="px-2 py-1 text-right font-normal">
                  {i18n.t('v2.settings.plannerImport.col_counts', 'Verarb./Übergangen')}
                </th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.run_id}
                  onClick={() => setOpenRunId(openRunId === run.run_id ? null : run.run_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ')
                      setOpenRunId(openRunId === run.run_id ? null : run.run_id);
                  }}
                  tabIndex={0}
                  className="cursor-pointer transition-colors"
                  style={{
                    borderTop: '1px solid var(--line-soft)',
                    color: 'var(--ink-2)',
                    background: openRunId === run.run_id ? 'var(--surface-2)' : undefined,
                  }}
                >
                  <td className="px-2 py-1.5">{new Date(run.uploaded_at).toLocaleString()}</td>
                  <td className="px-2 py-1.5 font-mono truncate max-w-[160px]">{run.file_name}</td>
                  <td className="px-2 py-1.5">
                    {i18n.t(`v2.settings.plannerImport.status.${run.status}`, run.status)}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {run.processed_count ?? '–'} / {run.skipped_count ?? '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openRunId && runDetailQuery.data && (
        <div
          className="rounded-lg p-3 space-y-2 text-xs"
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--line-soft)',
            color: 'var(--ink-2)',
          }}
        >
          <p className="font-medium" style={{ fontFamily: 'var(--f-mono)' }}>
            {runDetailQuery.data.file_name}
          </p>
          {runDetailQuery.data.log.length === 0 ? (
            <p style={{ color: 'var(--ink-3)' }}>
              {i18n.t('v2.settings.plannerImport.log_empty', 'Kein Log vorhanden.')}
            </p>
          ) : (
            <ul className="space-y-1 list-disc pl-4" style={{ color: 'var(--ink-3)' }}>
              {runDetailQuery.data.log.map((entry, i) => (
                <li key={`${entry.category}-${entry.salesforce_id ?? entry.row_ref}-${i}`}>
                  <code>{entry.category}</code>
                  {entry.row_ref ? ` [${entry.row_ref}]` : ''}: {entry.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {openRunId && runDetailQuery.isLoading && (
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {i18n.t('v2.common.loading', 'Lädt…')}
        </p>
      )}
    </div>
  );
}

function SettingsSection({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-xl border p-5 space-y-3"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div>
        <h2 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {label}
        </h2>
        <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-3)' }}>
          {hint}
        </p>
      </div>
      {children}
    </section>
  );
}

function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ id: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div
      className="inline-flex rounded-lg p-0.5 max-w-full flex-wrap"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
    >
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className="text-xs px-3 py-1.5 rounded-md transition-colors"
            style={{
              background: active ? 'var(--primary)' : 'transparent',
              color: active ? 'white' : 'var(--ink-2)',
              fontFamily: 'var(--f-mono)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
